import React from "react";
import PropTypes from "prop-types";
import { STATUS_TONE_CLASSES } from "../constants/order-status";

// StatusBadge — the one shared status-pill visual, used by Orders, B2B, Reorders (all
// read-only) and Quotations (interactive, via QuotationStatusControl).
//
// Before this existed, the exact same pill markup was hand-written three times
// (Orders' local StatusPill, B2B's inline copy, QuotationStatusControl's <button>) and
// had already drifted — B2B's copy was missing `whitespace-nowrap` and the icon gap.
// Tone is a semantic name from STATUS_TONE_CLASSES (see order-status.js), never a raw
// Tailwind class, so any status registry (order or quotation) can drive this the same way.
//
// The geometry below is the legacy PillEngine chip, verbatim from the filter chips on
// /all-orders and /quotations: `text-[10px] px-2 py-1 rounded-[10px] flex items-center
// gap-2 font-sans`. It is deliberately borderless — STATUS_TONE_CLASSES exposes only
// `bg` and `text` for that reason, so do not interpolate a third key here.
//
// `getStatusBadgeClassName` is exported on its own, not just this component, because
// QuotationStatusControl's trigger is a <button> with extra states (disabled, a chevron
// icon) that don't fit cleanly through a polymorphic `as` prop — it calls this directly
// instead of re-deriving the tone->class mapping itself.
export function getStatusBadgeClassName(tone, extra = "") {
    const classes = STATUS_TONE_CLASSES[tone] || STATUS_TONE_CLASSES.neutral;
    return `inline-flex items-center gap-2 whitespace-nowrap rounded-[10px] px-2 py-1 text-[10px] font-semibold font-sans ${classes.bg} ${classes.text} ${extra}`.trim();
}

function StatusBadge({ label, tone, className }) {
    return <span className={getStatusBadgeClassName(tone, className)}>{label}</span>;
}

StatusBadge.propTypes = {
    label: PropTypes.node.isRequired,
    tone: PropTypes.oneOf(["success", "warning", "info", "error", "neutral"]),
    className: PropTypes.string,
};

StatusBadge.defaultProps = {
    tone: "neutral",
    className: "",
};

export default StatusBadge;
